import { useState, useEffect, useMemo, useCallback } from "react";
import { apiGet } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Legend,
} from "recharts";
import { PageHeader, Card, SectionHeading } from "../components/dc";

const ALL_KEY = "All suppliers";
const PERIODS = [
  { value: "all", label: "All time" },
  { value: "12", label: "12m" },
  { value: "6", label: "6m" },
  { value: "3", label: "3m" },
];
const fmtPct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(1)}%`);
const pctColor = (v) => (v == null ? "#9d8fc0" : v > 0 ? "#fb7185" : v < 0 ? "#34d399" : "#9d8fc0");
// Line colours for opted-in supplier lines (composite is always cyan).
const LINE_COLORS = ["#ec4899", "#a855f7", "#34d399", "#fbbf24", "#f97316", "#c084fc", "#38bdf8", "#fb7185"];

export default function InflationIndex() {
  usePageTitle("Inflation Index");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState("all");
  const [picked, setPicked] = useState([]);     // opted-in supplier lines
  const [showMarkets, setShowMarkets] = useState(true);

  const togglePick = (name) =>
    setPicked((prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]));

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await apiGet("/dashboard/inflation-index", { timeout: 60000 });
      setData(res || { series: [], suppliers: [], summary: [], markets: [] });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const suppliers = useMemo(() => data?.suppliers || [], [data]);
  const markets = useMemo(() => data?.markets || [], [data]);
  const marketLabels = useMemo(() => markets.map((m) => m.label), [markets]);
  const marketMaps = useMemo(() => {
    const maps = {};
    for (const m of markets) {
      const byMonth = {};
      (m.series || []).forEach((p) => { if (p && p.month != null) byMonth[p.month] = p.value; });
      maps[m.label] = byMonth;
    }
    return maps;
  }, [markets]);

  // Period filter + rebase to 100 (index is multiplicative → rebasing = division).
  const series = useMemo(() => {
    let rows = data?.series || [];
    if (!rows.length) return [];
    if (period !== "all") {
      const n = parseInt(period, 10);
      rows = rows.slice(-(n + 1));
    }
    const base = {};
    return rows.map((row) => {
      const out = { month: row.month };
      for (const s of suppliers) {
        const v = row[s];
        if (v == null) continue;
        if (base[s] == null) base[s] = v;
        out[s] = Math.round((v / base[s]) * 1000) / 10;
        out[`${s}_n`] = row[`${s}_n`];
      }
      for (const label of marketLabels) {
        const mv = marketMaps[label]?.[row.month];
        if (mv == null) continue;
        if (base[label] == null) base[label] = mv;
        out[label] = Math.round((mv / base[label]) * 1000) / 10;
      }
      return out;
    });
  }, [data, suppliers, period, marketLabels, marketMaps]);

  const summaryRows = useMemo(() => {
    const marketSet = new Set(marketLabels);
    const keys = [...suppliers, ...marketLabels];
    const rows = [];
    for (const s of keys) {
      const pts = series.map((r) => r[s]).filter((v) => v != null);
      if (pts.length < 2) continue;
      const change = Math.round((pts[pts.length - 1] / pts[0] - 1) * 1000) / 10;
      const apiSummary = (data?.summary || []).find((x) => x.supplier === s);
      const lastRow = [...series].reverse().find((r) => r[s] != null);
      rows.push({
        supplier: s,
        isMarket: marketSet.has(s),
        change,
        yoy: apiSummary?.yoy_pct ?? null,
        months: pts.length,
        skus: lastRow ? lastRow[`${s}_n`] ?? null : null,
      });
    }
    return rows;
  }, [series, suppliers, data, marketLabels]);

  const selectableSuppliers = useMemo(
    () => suppliers.filter((s) => s !== ALL_KEY).sort((a, b) => a.localeCompare(b)),
    [suppliers]
  );
  const marketRows = useMemo(
    () => summaryRows.filter((r) => r.isMarket).sort((a, b) => Math.abs(b.change) - Math.abs(a.change)),
    [summaryRows]
  );
  const supplierRows = useMemo(
    () => summaryRows.filter((r) => !r.isMarket && r.supplier !== ALL_KEY).sort((a, b) => Math.abs(b.change) - Math.abs(a.change)),
    [summaryRows]
  );

  return (
    <div data-testid="inflation-index-page">
      <PageHeader title="Supplier Inflation Index" subtitle="Volume-weighted price inflation from your invoices — like-for-like SKUs only" />

      {loading ? (
        <Card style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-3)" }}>Computing index across all invoices…</Card>
      ) : error ? (
        <Card style={{ padding: "2rem", textAlign: "center" }} data-testid="inflation-error">
          <div style={{ color: "#fb7185", marginBottom: 8 }}>Failed to load inflation index</div>
          <button onClick={load} style={{ border: "none", background: "transparent", color: "#fb7185", cursor: "pointer", fontWeight: 600 }}>Retry</button>
        </Card>
      ) : !series.length ? (
        <Card style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-3)" }}>
          Not enough invoice history yet — the index needs the same SKUs purchased in at least two different months.
        </Card>
      ) : (
        <>
          <Card style={{ marginBottom: 20 }}>
            <SectionHeading
              title="Price Index"
              subtitle="Index starts at 100 · dashed reference = no change · composite always shown"
              right={
                <div style={{ display: "flex", gap: 8 }}>
                  {PERIODS.map((p) => {
                    const on = period === p.value;
                    return (
                      <div key={p.value} onClick={() => setPeriod(p.value)}
                        style={{ padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: on ? 700 : 500, cursor: "pointer", border: on ? "none" : "1px solid rgba(255,255,255,0.12)", background: on ? "var(--color-primary-16)" : "transparent", color: on ? "var(--color-primary-500)" : "var(--color-text-3)" }}>
                        {p.label}
                      </div>
                    );
                  })}
                </div>
              }
            />

            {/* Line selection: add supplier lines + toggle market benchmarks */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
              <select
                value=""
                onChange={(e) => { if (e.target.value) togglePick(e.target.value); }}
                style={{ background: "var(--color-bg-panel)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 9, padding: "8px 12px", color: "var(--color-text-1)", fontSize: 13.5, fontFamily: "var(--font-family)" }}
              >
                <option value="">+ Add supplier line…</option>
                {selectableSuppliers.filter((s) => !picked.includes(s)).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {picked.map((s, i) => (
                <span key={s} onClick={() => togglePick(s)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: "pointer", background: "rgba(255,255,255,0.06)", color: LINE_COLORS[i % LINE_COLORS.length], border: `1px solid ${LINE_COLORS[i % LINE_COLORS.length]}55` }}>
                  {s} ✕
                </span>
              ))}
              {marketRows.length > 0 && (
                <span onClick={() => setShowMarkets((v) => !v)}
                  style={{ padding: "5px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: "pointer", marginLeft: "auto", background: showMarkets ? "rgba(251,191,36,0.15)" : "transparent", color: showMarkets ? "#fbbf24" : "var(--color-text-3)", border: "1px solid rgba(251,191,36,0.35)" }}>
                  Benchmarks {showMarkets ? "on" : "off"}
                </span>
              )}
            </div>

            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: "#786a97", fontSize: 11 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
                  <YAxis tick={{ fill: "#786a97", fontSize: 11 }} axisLine={false} tickLine={false} domain={["auto", "auto"]} width={38} />
                  <ReferenceLine y={100} stroke="rgba(255,255,255,0.2)" strokeDasharray="4 4" />
                  <Tooltip contentStyle={{ background: "#17102b", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, color: "#f5f2fb" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey={ALL_KEY} stroke="#67e0f0" strokeWidth={2.5} dot={false} name="Composite" connectNulls />
                  {picked.map((s, i) => (
                    <Line key={s} type="monotone" dataKey={s} stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={2} dot={false} connectNulls />
                  ))}
                  {showMarkets && marketRows.map((m) => (
                    <Line key={m.supplier} type="monotone" dataKey={m.supplier} stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="5 4" dot={false} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, alignItems: "start" }}>
            <Card>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Market Benchmarks — Top Movers</div>
              {marketRows.length === 0 && <div style={{ color: "var(--color-text-3)", fontSize: 14, padding: "12px 0" }}>No market indexes uploaded — add them in Settings.</div>}
              {marketRows.map((b, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderTop: "1px solid rgba(255,255,255,0.06)", fontSize: 14 }}>
                  <div style={{ color: "#fbbf24" }}>{b.supplier}</div>
                  <div style={{ fontWeight: 700, color: pctColor(b.change), fontVariantNumeric: "tabular-nums" }}>{fmtPct(b.change)}</div>
                </div>
              ))}
            </Card>

            <Card>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Suppliers — Change Over Period</div>
              <div style={{ display: "grid", gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.7fr", padding: "0 0 8px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--color-text-4)", borderBottom: "1px solid var(--hairline)" }}>
                <div>Supplier</div><div>Period</div><div>YoY</div><div style={{ textAlign: "right" }}>SKUs</div>
              </div>
              {supplierRows.map((s, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.7fr", alignItems: "center", padding: "11px 0", borderBottom: "1px solid var(--hairline-soft)", fontSize: 13.5 }}>
                  <div style={{ fontWeight: 600 }}>{s.supplier}</div>
                  <div style={{ fontWeight: 700, color: pctColor(s.change), fontVariantNumeric: "tabular-nums" }}>{fmtPct(s.change)}</div>
                  <div style={{ color: "var(--color-text-3)", fontVariantNumeric: "tabular-nums" }}>{fmtPct(s.yoy)}</div>
                  <div style={{ textAlign: "right", color: "#786a97", fontVariantNumeric: "tabular-nums" }}>{s.skus ?? "—"}</div>
                </div>
              ))}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
